import { Fragment, useEffect, useMemo } from "react";

import { ContactSupportLink } from "@bokio/components/ContactSupportLink/ContactSupportLink";
import { Notice } from "@bokio/designsystem/components/Notice/Notice";
import Markdown from "@bokio/elements/Markdown/Markdown";
import GeneralLangFactory from "@bokio/lang/GeneralLangFactory";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { withErrorMessageFromEnvelope } from "@bokio/mobile-web-shared/core/utils/loaderHelpers";
import { formatMessage } from "@bokio/shared/utils/format";
import { trackIfContainsHtmlEncodedText } from "@bokio/utils/t";

import type { LoadingProps } from "./LoadingProps";
import type { ErrorEnvelope } from "@bokio/mobile-web-shared/core/model/types/envelope";
import type { RequestState } from "@bokio/mobile-web-shared/services/api/requestState";
import type { ReactNode } from "react";

import * as styles from "./loading.scss";

export interface RenderRequestErrorData<TError> {
	Error: TError | undefined;
	ErrorMessage: string | undefined;
}

interface RenderRequestErrorProps<TError> extends LoadingProps {
	showWhenLoaded?: ReactNode;
	noNoticeTopMargin?: boolean;
	noticeMarginBottom?: boolean;
	testId?: string;
	appendToError?: ReactNode; // Can be used for adding links, tooltips or extra interactive elements to error messages from the backend
	whenError?: "useDefault" | ((result: RenderRequestErrorData<TError>) => ReactNode);
}

export const RenderRequestError = <TError,>({
	request,
	requests,
	children,
	showWhenLoaded,
	noNoticeTopMargin,
	noticeMarginBottom,
	hideSoftErrors = false,
	testId,
	appendToError,
	whenError = "useDefault",
}: RenderRequestErrorProps<TError>) => {
	const allRequests = useMemo(() => {
		let requestsList = [...(requests || []), ...(request ? [request] : [])];

		if (!hideSoftErrors) {
			requestsList = requestsList.map(r => withErrorMessageFromEnvelope(r));
		}

		return requestsList;
	}, [request, requests, hideSoftErrors]);

	const generalLang = GeneralLangFactory();

	const isLoading = allRequests.length === 0 || allRequests.some(r => r.isLoading);
	const allDone = allRequests.length > 0 && allRequests.every(r => !r.isLoading);

	const hasErrors = allRequests.some(r => r.errorMessage);

	useEffect(() => {
		allRequests.forEach(r => {
			if (r.errorMessage) {
				trackIfContainsHtmlEncodedText(r.errorMessage, "RenderRequestError");
			}
		});
	}, [allRequests]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const renderErrorWithoutErrorCode = (r: RequestState<any>) => {
		if (whenError === "useDefault") {
			return (
				<Notice
					key={r.errorMessage}
					color="error"
					margin={[!noNoticeTopMargin && "top", noticeMarginBottom && "bottom"]}
					testId={testId || "LoadingWithError_ErrorMessage"}
				>
					<>
						{r.errorMessage && <Markdown markdownContent={r.errorMessage} />}
						{appendToError}
					</>
				</Notice>
			);
		} else {
			let error: RenderRequestErrorData<TError>;

			const errorEnvelope = r.data as ErrorEnvelope<TError> | undefined | null;

			if (!hideSoftErrors && errorEnvelope && !errorEnvelope.Success && errorEnvelope.ErrorMessage !== undefined) {
				// Envelope errors
				error = {
					Error: errorEnvelope.Error,
					ErrorMessage: errorEnvelope.ErrorMessage,
				};
			} else {
				// Other errors
				error = {
					Error: undefined,
					ErrorMessage: r.errorMessage,
				};
			}
			return whenError(error);
		}
	};

	if (isLoading) {
		return <>{children}</>;
	}

	if (!hasErrors) {
		return allDone && showWhenLoaded;
	}

	return (
		<>
			{allRequests.map((r, index) => (
				<Fragment key={r.errorMessage ?? index}>
					{r &&
						// Previously we only had the [BE-1234-5678] error codes. And when we did, we always wanted a generic message.
						// Now we always have a referenceId in the same property, but the error message could be localized and ment to be shown.
						r.errorMessage &&
						(r.errorCode && r.errorCode.startsWith("[BE-") ? (
							<Notice
								key={r.errorMessage}
								color="error"
								margin={[!noNoticeTopMargin && "top", noticeMarginBottom && "bottom"]}
								testId={testId || "LoadingWithError_ErrorMessage"}
							>
								<>
									<div>
										{formatMessage(
											generalLang.ErrorMessage_GenericWithErrorCode,
											() => (
												<ContactSupportLink
													area={m.Contracts.SupportFormArea.NotSet}
													messagePrefill={"(" + generalLang.ErrorCode_InMessageToSupport + r.errorCode + ")"}
												>
													{generalLang.ErrorMessage_GenericWithErrorCode_ContactSupportPart}
												</ContactSupportLink>
											),
											() => (
												<strong className={styles.emphasizedText}>{r.errorCode}</strong>
											),
										)}
									</div>
									{appendToError}
								</>
							</Notice>
						) : (
							renderErrorWithoutErrorCode(r)
						))}
				</Fragment>
			))}
		</>
	);
};
